import React, { useCallback, createContext, useContext, useState, useEffect } from 'react';
import { School, SchoolSettings, SchoolFeature } from '../types';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

interface SchoolContextType {
    currentSchool: School | null;
    schoolSettings: SchoolSettings | null;
    loading: boolean;
    switchSchool: (schoolId: string) => Promise<void>;
}

const SchoolContext = createContext<SchoolContextType | undefined>(undefined);

export const DEFAULT_SCHOOL_ID = 'kca-main';

// Synchronously check if the current hostname looks like a whitelabel subdomain.
// This is used to block rendering the default MEF site while the async school
// resolution is still in flight.
function hostnameNeedsResolution(): boolean {
    const hostname = window.location.hostname;
    if (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.endsWith('.web.app') ||
        hostname.endsWith('.firebaseapp.com')
    ) return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get('s') || params.get('school')) return true;
    // Any real external hostname is a custom domain / whitelabel subdomain
    return true;
}

export const SchoolProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentSchoolId, setCurrentSchoolId] = useState<string>(DEFAULT_SCHOOL_ID);
    const [currentSchool, setCurrentSchool] = useState<School | null>(null);
    const [schoolSettings, setSchoolSettings] = useState<SchoolSettings | null>(null);
    const [loading, setLoading] = useState(true);
    // schoolIdReady: false while async resolution is in-flight on a custom hostname.
    // Prevents the data listener from starting (and the default MEF site from flashing)
    // until we know which school we're on.
    const [schoolIdReady, setSchoolIdReady] = useState<boolean>(!hostnameNeedsResolution());

    // Resolve school from hostname or URL parameter on mount (white-label support)
    useEffect(() => {
        if (schoolIdReady) return; // already resolved synchronously (localhost / .web.app)
        const resolveSchool = async () => {
            // 1. URL parameter routing: ?s=subdomain or ?school=schoolId
            //    meflms.muyikaratu.com is already connected — share meflms.muyikaratu.com?s=nextg
            const params = new URLSearchParams(window.location.search);
            const sParam = params.get('s') || params.get('school');
            if (sParam) {
                try {
                    const bySubdomain = query(collection(db, 'schools'), where('subdomain', '==', sParam));
                    const subSnap = await getDocs(bySubdomain);
                    if (!subSnap.empty) { setCurrentSchoolId(subSnap.docs[0].id); setSchoolIdReady(true); return; }
                    setCurrentSchoolId(sParam);
                    setSchoolIdReady(true);
                    return;
                } catch (err) {
                    console.error('School param resolution failed:', err);
                }
            }

            // 2. Custom hostname detection (requires DNS + Firebase Hosting custom domain)
            const hostname = window.location.hostname;
            try {
                // Exact custom domain match (e.g. portal.greenfield.edu.ng)
                const byDomain = query(collection(db, 'schools'), where('customDomain', '==', hostname));
                const domainSnap = await getDocs(byDomain);
                if (!domainSnap.empty) {
                    setCurrentSchoolId(domainSnap.docs[0].id);
                    setSchoolIdReady(true);
                    return;
                }

                // Subdomain prefix match (e.g. nextg.meflms.muyikaratu.com → 'nextg')
                const parts = hostname.split('.');
                if (parts.length >= 3) {
                    const subPrefix = parts[0];
                    const bySubdomain = query(collection(db, 'schools'), where('subdomain', '==', subPrefix));
                    const subSnap = await getDocs(bySubdomain);
                    if (!subSnap.empty) {
                        setCurrentSchoolId(subSnap.docs[0].id);
                        setSchoolIdReady(true);
                        return;
                    }
                }
            } catch (err) {
                console.error('Hostname resolution failed:', err);
            }
            // No school matched — fall back to default
            setSchoolIdReady(true);
        };
        resolveSchool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Safety fallback: Ensure schoolIdReady & loading never stay stuck
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!schoolIdReady) {
                console.warn('School resolution timed out - falling back to default school');
                setSchoolIdReady(true);
            }
            if (loading) {
                console.warn('SchoolContext loading timed out - forcing loading to false');
                setLoading(false);
            }
        }, 3000);
        return () => clearTimeout(timer);
    }, [schoolIdReady, loading]);

    useEffect(() => {
        if (!schoolIdReady) return; // wait for async resolution before subscribing to any school data
        setLoading(true);
        let unsubscribe: (() => void) | null = null;

        if (currentSchoolId === DEFAULT_SCHOOL_ID) {
            // Live listener on the legacy settings document for kca-main
            unsubscribe = onSnapshot(
                doc(db, 'settings', 'school'),
                (snap) => {
                    const settings = snap.exists() ? snap.data() as SchoolSettings : null;
                    setCurrentSchool({
                        id: DEFAULT_SCHOOL_ID,
                        name: settings?.schoolName || 'MEF EduSuite',
                        subscriptionStatus: 'active',
                        subscriptionPlan: 'premium',
                        subscriptionExpiry: Date.now() + 31536000000,
                        createdAt: Date.now(),
                        settings: settings || undefined,
                    });
                    setSchoolSettings(settings);
                    setLoading(false);
                },
                (err) => {
                    console.error('SchoolContext snapshot error:', err);
                    setLoading(false);
                }
            );
        } else {
            // Live listener on the school document for partner schools
            unsubscribe = onSnapshot(
                doc(db, 'schools', currentSchoolId),
                (snap) => {
                    if (snap.exists()) {
                        const data = snap.data() as School;
                        setCurrentSchool(data);
                        setSchoolSettings(data.settings || null);
                    } else {
                        const fallbackName = currentSchoolId
                            .split('-')
                            .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(' ');
                        const fallback: School = {
                            id: currentSchoolId, name: fallbackName,
                            contactEmail: '', adminName: 'School Administrator',
                            subscriptionStatus: 'active', subscriptionPlan: '1-year',
                            subscriptionExpiry: Date.now() + 31536000000,
                            createdAt: Date.now(),
                            settings: { schoolName: fallbackName },
                        };
                        setCurrentSchool(fallback);
                        setSchoolSettings(fallback.settings || null);
                    }
                    setLoading(false);
                },
                (err) => {
                    console.error('SchoolContext snapshot error:', err);
                    setLoading(false);
                }
            );
        }

        return () => { if (unsubscribe) unsubscribe(); };
    }, [currentSchoolId, schoolIdReady]);

    // Once a school resolves, prefer its own uploaded logo for the browser-tab favicon
    // over the generic brand emoji applyBrand() sets at boot (src/whitelabel.ts) — this
    // can only happen here, reactively, since school data isn't known until after the
    // async hostname resolution above completes. The PWA "Add to Home Screen" icon is
    // handled separately (functions/src/index.ts's dynamicManifest), since a manifest is
    // fetched before any of this JS runs.
    useEffect(() => {
        const logo = currentSchool?.logoUrl || schoolSettings?.logoDataUrl;
        if (!logo) return;
        let favicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
        if (!favicon) {
            favicon = document.createElement('link');
            favicon.rel = 'icon';
            document.head.appendChild(favicon);
        }
        favicon.href = logo;
    }, [currentSchool?.logoUrl, schoolSettings?.logoDataUrl]);

    const switchSchool = async (schoolId: string) => {
        setCurrentSchoolId(schoolId);
    };

    return (
        <SchoolContext.Provider value={{ currentSchool, schoolSettings, loading, switchSchool }}>
            {children}
        </SchoolContext.Provider>
    );
};

/** Like useSchool(), but returns undefined instead of throwing outside a SchoolProvider
 *  (used by shared components such as the lesson video players). */
export const useSchoolOptional = () => useContext(SchoolContext);

/**
 * Is this module switched on for the school? The rule lives here and nowhere else: a school with
 * no list at all predates the module system, and keeps everything rather than losing it silently.
 */
export const hasFeature = (enabled: SchoolFeature[] | undefined, feature: SchoolFeature) =>
    !enabled || enabled.includes(feature);

/** hasFeature bound to the school the signed-in user belongs to. */
export const useFeature = () => {
    const enabled = useContext(SchoolContext)?.currentSchool?.enabledFeatures;
    return useCallback((feature: SchoolFeature) => hasFeature(enabled, feature), [enabled]);
};

export const useSchool = () => {
    const context = useContext(SchoolContext);
    if (context === undefined) {
        throw new Error('useSchool must be used within a SchoolProvider');
    }
    return context;
};
